// Copyright 2026 Granite (granite.md). All rights reserved.
// Proprietary and confidential. Unauthorized copying is prohibited.

/** English UI strings: The vault workspace: dialogs, history, import, settings (workspace/ui, first half). Merged into the catalog by messages.en.ts. */
export const vaultUi = {
  // VaultSettingsDialog.tsx
  "vaultUi.settings.title": "Vault settings",
  "vaultUi.settings.vaultName": "Vault name",
  "vaultUi.settings.dailyFolder": "Daily notes folder",
  "vaultUi.settings.dailyFolderHelp": "Where today's note is created.",
  "vaultUi.settings.templatesFolder": "Templates folder",
  "vaultUi.settings.templatesFolderHelp": "Notes here can be inserted as templates. A note named Daily is used for daily notes.",
  "vaultUi.settings.attachmentsFolder": "Attachments folder",
  "vaultUi.settings.attachmentsFolderHelp": "Where pasted and dropped files are saved.",
  "vaultUi.settings.cancel": "Cancel",
  "vaultUi.settings.save": "Save",
  "vaultUi.sharing.title": "Sharing",
  "vaultUi.sharing.intro": "Workspace owners and admins can open every shared vault, except folders members keep private. Give others access to the whole vault, one folder, or one note. To give a folder or note to some teams or people only, right-click it and choose Access.",
  "vaultUi.sharing.sharedWith": "Shared with",
  "vaultUi.sharing.group": "Team {name}",
  "vaultUi.sharing.someone": "Someone",
  "vaultUi.sharing.wholeVault": "whole vault",
  "vaultUi.sharing.remove": "Remove",
  "vaultUi.sharing.share": "Share",
  "vaultUi.sharing.who": "Person or team",
  "vaultUi.sharing.choose": "Choose",
  "vaultUi.sharing.access": "Access",
  "vaultUi.sharing.accessRead": "read",
  "vaultUi.sharing.accessComment": "comment",
  "vaultUi.sharing.accessWrite": "write",
  "vaultUi.sharing.accessAdmin": "admin",
  "vaultUi.sharing.folder": "Folder or note (optional)",
  "vaultUi.export.title": "Export",
  "vaultUi.export.intro": "Download every note and file in this vault as plain files in a zip.",
  "vaultUi.export.download": "Download vault",
  "vaultUi.export.upgrade": "Downloading a vault as a zip is part of Pro and Business.",
  "vaultUi.export.seePlans": "See plans",

  // History.tsx
  "vaultUi.history.diffRange": "From {from} to {to}:",
  "vaultUi.history.added": "{count} added",
  "vaultUi.history.removed": "{count} removed",
  "vaultUi.history.lines.one": "line",
  "vaultUi.history.lines.other": "lines",
  "vaultUi.history.changes": "Changes",
  "vaultUi.history.empty": "(empty)",
  "vaultUi.history.addedLine": "Added: ",
  "vaultUi.history.removedLine": "Removed: ",
  "vaultUi.history.deleted": "Deleted",
  "vaultUi.history.deletedHere": "Deleted on this device",
  "vaultUi.history.deletedElsewhere": "Deleted on another device",
  "vaultUi.history.moved": "Moved to {path}",
  "vaultUi.history.movedHere": "Moved to {path} on this device",
  "vaultUi.history.movedElsewhere": "Moved to {path} on another device",
  "vaultUi.history.edited": "Edited",
  "vaultUi.history.editedHere": "Edited on this device",
  "vaultUi.history.editedElsewhere": "Edited on another device",
  "vaultUi.history.title": "Version history: {name}",
  "vaultUi.history.loadingVersions": "Loading versions…",
  "vaultUi.history.none": "No history yet. Versions appear once the note has synced.",
  "vaultUi.history.upgrade": "Version history is part of Pro and Business: see and restore earlier versions of every note.",
  "vaultUi.history.seePlans": "See plans",
  "error.plan_limit_version_history": "Version history is part of Pro and Business.",
  "vaultUi.history.versions": "Versions",
  "vaultUi.history.current": "(current)",
  "vaultUi.history.choose": "Choose a version to see what changed since then.",
  "vaultUi.history.deletedVersion": "This version is the note being deleted; choose an earlier one.",
  "vaultUi.history.loading": "Loading…",
  "vaultUi.history.now": "now",
  "vaultUi.history.restore": "Restore this version",
  "vaultUi.history.conflictCopy": "This is a conflict copy of “{name}”, made when two devices changed it at once.",
  "vaultUi.history.compare": "Compare",
  "vaultUi.history.otherVersions.one": "Another version of this note came from a device that edited it at the same time.",
  "vaultUi.history.otherVersions.other": "{count} other versions of this note came from a device that edited it at the same time.",
  "vaultUi.history.conflictTitle": "Resolve conflict: {name}",
  "vaultUi.history.conflictDescription": "Two devices changed this note at the same time. Lines only in the other version are marked as added.",
  "vaultUi.history.keepBoth": "Keep both",
  "vaultUi.history.useOther": "Use the other version",
  "vaultUi.history.keepThis": "Keep this version",
  "vaultUi.history.thisNote": "this note",
  "vaultUi.history.otherVersion": "the other version ({name})",

  // ImportDialog.tsx
  "vaultUi.import.sourceObsidian": "Obsidian vault",
  "vaultUi.import.sourceNotion": "Notion export",
  "vaultUi.import.sourceMarkdown": "Markdown files",
  "vaultUi.import.readError": "Could not read {name}: {error}",
  "vaultUi.import.title": "Import notes",
  "vaultUi.import.description": "From an Obsidian vault, a Notion export (Markdown), or any folder of Markdown files. Links keep working.",
  "vaultUi.import.done": "Done",
  "vaultUi.import.cancel": "Cancel",
  "vaultUi.import.submit": "Import",
  "vaultUi.import.submitFiles.one": "Import {count} file",
  "vaultUi.import.submitFiles.other": "Import {count} files",
  "vaultUi.import.result": "Imported {notes} notes and {canvases} canvases. They are syncing now.",
  "vaultUi.import.skippedCount": "{count} skipped",
  "vaultUi.import.chooseFolder": "Choose a folder…",
  "vaultUi.import.reading": "Reading files… {done} of {total}",
  "vaultUi.import.readingZip": "Reading the zip…",
  "vaultUi.import.readingLabel": "Reading files",
  "vaultUi.import.chooseZip": "Choose a .zip…",
  "vaultUi.import.folderInput": "Folder to import",
  "vaultUi.import.defaultFolder": "Folder",
  "vaultUi.import.zipInput": "Zip file to import",
  "vaultUi.import.notes.one": "{count} note",
  "vaultUi.import.notes.other": "{count} notes",
  "vaultUi.import.canvases.one": "{count} canvas",
  "vaultUi.import.canvases.other": "{count} canvases",
  "vaultUi.import.summary": "{name}: {source} with {notes} and {canvases}.",
  "vaultUi.import.willSkip": "{count} will be skipped.",
  "vaultUi.import.intoFolder": "Import into folder",
  "vaultUi.import.intoFolderHelp": "Leave empty to import at the top of the vault. Existing notes are never overwritten.",
  "vaultUi.import.progress": "Import progress",
  "vaultUi.import.rulesTitle": "Import works on every plan. Files are imported when:",
  "vaultUi.import.ruleType": "they are notes (.md) or canvases (.canvas); images, PDFs, CSV and other files are left out",
  "vaultUi.import.ruleText": "notes are Markdown (.md) saved as UTF-8 text",
  "vaultUi.import.ruleNames": "each name is at most 255 bytes and each path at most 1024 (characters like : or ? are replaced)",
  "vaultUi.import.ruleSize": "each file is at most {size} on your plan",
  "vaultUi.import.ruleSizePlan": "each file fits your plan's file size limit",
  "vaultUi.import.ruleSystem": "they are not system or trash files (.DS_Store, .git, .trash)",

  // KnowledgeBaseSettings.tsx
  "vaultUi.kb.title": "Knowledge base",
  "vaultUi.kb.intro": "Semantic search and AI tools find passages in the notes and documents included here.",
  "vaultUi.kb.status": "Index status",
  "vaultUi.kb.unavailable": "Not available on this plan, or turned off.",
  "vaultUi.kb.notIndexed": "Not in the knowledge base.",
  "vaultUi.kb.indexed": "{documents} files, {chunks} passages indexed.",
  "vaultUi.kb.indexedPending": "{documents} files, {chunks} passages indexed; {pending} waiting.",
  "vaultUi.kb.include": "Include this vault",
  "vaultUi.kb.excluded": "Folders left out",
  "vaultUi.kb.includeFolder": "Include",
  "vaultUi.kb.excludeForm": "Leave a folder out",
  "vaultUi.kb.folder": "Folder",
  "vaultUi.kb.folderPlaceholder": "Private/Journal",
  "vaultUi.kb.excludeFolder": "Leave out",
  "vaultUi.kb.excludeDrive": "Leave Google Drive references out",
  "vaultUi.kb.failures": "Not indexed",
  "vaultUi.kb.failuresList": "Files not indexed",
  "vaultUi.kb.reindex": "Index again",

  // Dialogs.tsx
  "vaultUi.dialogs.createNote": "Create note “{name}”",
  "vaultUi.dialogs.switcherTitle": "Quick switcher",
  "vaultUi.dialogs.switcherPlaceholder": "Find or create a note…",
  "vaultUi.dialogs.switcherEmpty": "Type a name to create a note.",
  "vaultUi.dialogs.paletteTitle": "Command palette",
  "vaultUi.dialogs.palettePlaceholder": "Type a command…",
  "vaultUi.dialogs.paletteEmpty": "No matching commands.",
  "vaultUi.dialogs.templateTitle": "Insert template",
  "vaultUi.dialogs.templatePlaceholder": "Find a template…",
  "vaultUi.dialogs.templateEmpty": "No templates. Notes in the Templates folder appear here.",
  "vaultUi.dialogs.renameFolder": "Rename folder",
  "vaultUi.dialogs.newFolder": "New folder",
  "vaultUi.dialogs.newFolderIn": "Inside {folder}. It starts with one untitled note.",
  "vaultUi.dialogs.folderExists": "There is already a folder with this name.",
  "vaultUi.dialogs.create": "Create",
  "vaultUi.dialogs.renameNote": "Rename note",
  "vaultUi.dialogs.name": "Name",
  "vaultUi.dialogs.cancel": "Cancel",
  "vaultUi.dialogs.rename": "Rename",
  "vaultUi.dialogs.deleteFolderTitle": "Delete folder “{name}”?",
  "vaultUi.dialogs.deleteNoteTitle": "Delete “{name}”?",
  "vaultUi.dialogs.deleteFolderDescription.one": "This deletes {count} file on every device. Earlier versions stay in version history.",
  "vaultUi.dialogs.deleteFolderDescription.other": "This deletes {count} files on every device. Earlier versions stay in version history.",
  "vaultUi.dialogs.deleteNoteDescription": "This deletes the note on every device. Earlier versions stay in version history.",
  "vaultUi.dialogs.delete": "Delete",

  // PublishDialog.tsx
  "vaultUi.publish.notSynced": "This note has not synced yet. Try again in a moment.",
  "vaultUi.publish.title": "Publish “{name}”",
  "vaultUi.publish.description": "A published note is a web page anyone with the link can read. It shows the note as it is now and updates as you edit.",
  "vaultUi.publish.checking": "Checking…",
  "vaultUi.publish.publishedAt": "Published at",
  "vaultUi.publish.publishedProtectedAt": "Published with a password at",
  "vaultUi.publish.copied": "Copied",
  "vaultUi.publish.copyLink": "Copy link",
  "vaultUi.publish.unpublish": "Unpublish",
  "vaultUi.publish.address": "Page address",
  "vaultUi.publish.addressHelp": "Letters, digits, and dashes. Leave empty for a random address.",
  "vaultUi.publish.requirePassword": "Require a password",
  "vaultUi.publish.newPassword": "New password (leave empty to keep it)",
  "vaultUi.publish.close": "Close",
  "vaultUi.publish.update": "Update",
  "vaultUi.publish.publish": "Publish",

  // CommentsPanel.tsx
  "vaultUi.comments.needsSync": "Comments need a synced vault.",
  "vaultUi.comments.openNote": "Open a note to see its comments.",
  "vaultUi.comments.notSynced": "The note syncs before it can be commented on.",
  "vaultUi.comments.newComment": "New comment",
  "vaultUi.comments.removeQuote": "(remove)",
  "vaultUi.comments.comment": "Comment",
  "vaultUi.comments.placeholderSelection": "Comment on the selection… (@email to mention)",
  "vaultUi.comments.placeholderNote": "Comment on the note… (@email to mention)",
  "vaultUi.comments.list": "Comments",
  "vaultUi.comments.showInNote": "Show in the note",
  "vaultUi.comments.anchorGone": "The commented text was changed or removed",
  "vaultUi.comments.gone": "(gone)",
  "vaultUi.comments.someone": "Someone",
  "vaultUi.comments.deleted": "deleted",
  "vaultUi.comments.reply": "Reply",
  "vaultUi.comments.replyPlaceholder": "Reply…",
  "vaultUi.comments.reopen": "Reopen",
  "vaultUi.comments.resolve": "Resolve",
  "vaultUi.comments.delete": "Delete",
  "vaultUi.comments.deleteConfirm": "Delete this comment? Replies to it stay.",
  "vaultUi.comments.empty": "No comments yet. Select text and run “Comment on selection”.",
  "vaultUi.share.title": "Share “{name}”",
  "vaultUi.share.description": "Invite someone by email to edit this note. They see this note only, not the rest of the vault.",
  "vaultUi.share.form": "Invite by email",
  "vaultUi.share.email": "Email address",
  "vaultUi.share.emailOrName": "Name or email address",
  "vaultUi.share.suggestions": "People in your workspace",
  "vaultUi.share.invite": "Invite",
  "vaultUi.share.loading": "Loading…",
  "vaultUi.share.none": "Not shared with anyone yet.",
  "vaultUi.share.people": "People with access",
  "vaultUi.share.canEdit": "Can edit",
  "vaultUi.share.remove": "Remove",
  "vaultUi.share.removeOne": "Stop sharing with {email}",
  "vaultUi.share.seePlans": "See plans",
  "vaultUi.share.notSynced": "This note has not synced yet. Try again in a moment.",
  "vaultUi.share.proNote": "Shared notes open for people on Pro. Anyone else is asked to upgrade first.",
  "shared.title": "Shared with me",
  "shared.sharedBy": "Shared by {name}",
  "shared.gone": "This note is no longer shared with you.",
  "shared.closedTitle": "{name} shared a note with you",
  "shared.upgradeRequired": "Shared notes open on Granite Pro. Upgrade to read and edit it.",
  "shared.emailUnverified": "Confirm your email address first: shared notes open for confirmed addresses only.",
  "shared.ownerPlan": "{name}'s plan doesn't include shared notes at the moment, so it can't be opened.",
  "shared.upgrade": "Upgrade to Pro",
  "shared.live": "Editing live",
  "shared.saving": "Saving…",
  "shared.saved": "Saved",
  "shared.replaced": "Someone else saved a newer version; it is shown now.",
  "shared.closed": "This note is no longer shared with you.",

  // VaultMenu.tsx, VaultAccessDialog.tsx: right-click on a vault in the vault lists
  "vaultMenu.rename": "Rename…",
  "vaultMenu.delete": "Delete vault…",
  "vaultMenu.renameTitle": "Rename vault",
  "vaultMenu.deleteTitle": "Delete {name}?",
  "vaultMenu.deleteDescription": "The vault and its notes move to the trash and are deleted permanently after 30 days.",
  "vaultMenu.deleteFolder": "Its folder in Documents › Granite moves to the Trash on this computer.",
  "vaultMenu.deleteConfirm": "Delete vault",
  "vaultMenu.folderFailed": "The vault was deleted, but its folder could not be moved to the Trash: {error}",
  "vaultMenu.accessIntro": "Teams and people who can open the whole vault. Workspace owners and admins always can. To give a single folder or note, right-click it in the vault and choose Access.",
  "vaultMenu.accessNone": "Nobody else has access to the whole vault yet.",
  "vaultMenu.accessDenied": "Only the vault's admins can see and change who has access to it.",
  "vaultMenu.levelOf": "Access for {name}",
} as const;
